import {
  ChronologicalData,
  GroupedData,
} from '../utils/chronological-data.interface';
import { DateFormatter } from '../date-formatter.class';
import { GroupBy } from '../utils/group-by.enum';
import { GroupByStrategyBase } from './group-strategy-base.class';
import { GroupedChronologicalData } from '../utils/grouped-chronological-data.class';
import { DatesGroup } from '../utils/dates-group.interface';
import { getDataByDates } from '../utils/get-data-by-dates';

/* It takes an array of dates and returns an array of dates grouped by year */
export class GroupByYearStrategy<T> extends GroupByStrategyBase<T> {
  locale: string | undefined;

  /**
   * It takes an array of chronological data, groups it by a given groupBy parameter,
   * and returns a grouped chronological data object
   * @param {ChronologicalData<T>[]} data - ChronologicalData<T>[]
   * @param {GroupBy} groupBy - GroupBy - this is the type of grouping you want to
   * do. It can be one of the following:
   * @param {string} [locale] - The locale to use for the date formatting.
   * @returns A GroupedChronologicalData<T> object.
   */
  group(
    data: ChronologicalData<T>[],
    groupBy: GroupBy,
    locale?: string
  ): GroupedChronologicalData<T> {
    this.locale = locale;
    const group = new GroupedChronologicalData<T>(groupBy);
    const dates = data.map((value) => value.date);

    const datesByYear = this.groupDatesByYear(dates);
    const grupedData = datesByYear.map((weekGroup) => {
      const label = weekGroup.label;
      const dates = weekGroup.dates;

      const dataGroup = getDataByDates(data, dates, this.locale);

      return {
        label,
        dates,
        data: dataGroup,
      } as GroupedData<T>;
    });
    group.data = grupedData;

    return group;
  }

  /**
   * It takes an array of dates and returns an array of dates grouped by year
   * @param {Date[]} dates - Date[] - an array of dates to group
   * @returns An array of objects with a label and dates property.
   */
  private groupDatesByYear(dates: Date[]): DatesGroup[] {
    const groupsByYear: DatesGroup[] = [];
    const years = Array.from(
      new Set(
        dates.map(
          (date) => new DateFormatter(this.locale).getYearString(date) as string
        )
      )
    );

    years.forEach((month) => {
      const group: DatesGroup = {
        label: month,
        dates: dates.filter((date) => {
          const monthDate = new DateFormatter(this.locale).getYearString(
            date
          ) as string;
          return monthDate === month;
        }),
      };

      groupsByYear.push(group);
    });

    return groupsByYear;
  }
}
